import './ReportsDashboard.scss'
import React, { useState } from 'react'
import { useSelector } from 'react-redux'

import { Modal, Row, Col } from 'react-bootstrap'
import { Title } from 'src/components'
import CSVLoaderV2, { CATEGORY_UPLOAD } from 'src/components/CSVLoader/CSVLoaderV2'

import ReportCard from './ReportCard'
import {
  usePermissionFromPlan,
  plansForFeature,
  PlanFeature,
} from 'src/hooks/usePermissionFromPlan'

import IconProducts from 'src/assets/v2/icon-products.svg'
import IconSales from 'src/assets/v2/icon-sales.svg'
import IconUsers from 'src/assets/v2/icon-users.svg'
import IconClients from 'src/assets/v2/icon-clients.svg'
import IconExpenses from 'src/assets/v2/icon-expenses.svg'
import IconWaste from 'src/assets/v2/icon-inventory.svg'
import IconInventory from 'src/assets/v2/icon-inventory.svg'
import IconActPas from 'src/assets/v2/icon-active-pasive.svg'
import IconInvoice from 'src/assets/v2/icon-help.svg'

import { selectCurrentModule } from 'src/selectors/user.selector'
import { isAllowed } from 'src/selectors/modules.selector'
import { selectCompanyIsLimit } from 'src/selectors/company.selector'

import { reportPermissions } from 'src/enums/permissions'
import ParkingIcon from 'src/assets/images/svg/parking.svg'

interface Report {
  name: string
  link: string
  icon: string
  info: string
  show: boolean
  /** Función limitada por plan; si el plan no la habilita, la card se bloquea. */
  feature?: PlanFeature
  bullets?: string[]
  onClick?: () => void
}

const ReportsDashboard = () => {
  const isLimit: boolean = useSelector(selectCompanyIsLimit)
  const module = useSelector(selectCurrentModule)
  const planPermissions = usePermissionFromPlan()

  // PERMISSIONS
  const itemsSoldSegmented = useSelector(state =>
    isAllowed(state, [reportPermissions.itemsSoldSegmented]),
  )

  const inventoryReport = useSelector(state =>
    isAllowed(state, [reportPermissions.inventoryKardex]),
  )
  const inventoryHistory = useSelector(state =>
    isAllowed(state, [reportPermissions.inventoryHistorical]),
  )
  const inOutUser = useSelector(state => isAllowed(state, [reportPermissions.reportUser]))
  const totalSell = useSelector(state => isAllowed(state, [reportPermissions.totalSell]))
  const patrimonial = useSelector(state =>
    isAllowed(state, [reportPermissions.patrimonial]),
  )
  const waste = useSelector(state => isAllowed(state, [reportPermissions.waste]))
  const expense = useSelector(state => isAllowed(state, [reportPermissions.expense]))
  const clients = useSelector(state => isAllowed(state, [reportPermissions.client]))
  const purchase = useSelector(state => isAllowed(state, [reportPermissions.purchase]))
  const clientLog = useSelector(state => isAllowed(state, [reportPermissions.clientLog]))
  const segmentation = useSelector(state =>
    isAllowed(state, [reportPermissions.segmentation]),
  )
  const branchSales = useSelector(state => isAllowed(state, [reportPermissions.branch]))

  const [conciliationReport, setConciliationReport] = useState({
    show: false,
    nullify: false,
  })

  const getReports = (): Report[] => {
    return [
      {
        name: 'Reporte de ventas por ítems',
        link: 'productos',
        icon: IconProducts,
        info: 'Ventas Totales por periodo',
        show: itemsSoldSegmented,
        bullets: [
          'Detalle de cada ítem vendido en el periodo que elijas',
          'Gráfica de barras ordenada de mayor a menor venta',
          'Ranking por monto para identificar tus productos más vendidos',
        ],
      },
      {
        name: 'Precios por producto en un periodo',
        link: 'productos/historial',
        icon: IconProducts,
        info: 'Precio de productos en un periodo',
        show: false, //pricePeriod,
      },
      {
        name: 'Reporte de inventarios',
        link: 'inventario/kardex',
        icon: IconInventory,
        info: 'Kardex de bodegas',
        show: inventoryReport,
        feature: 'kardex',
        bullets: [
          'Kardex: cantidad inicial, ingresos, egresos y saldo final por ítem de una bodega',
          'Inventario a la fecha: existencias hasta una fecha indicada en múltiples bodegas',
          'Descarga ambos reportes en formato XLSX',
        ],
      },
      {
        // TODO: revisar que hace
        name: 'Productos',
        link: 'products',
        icon: IconProducts,
        info: 'Ventas Totales por periodo',
        show: module === 11000,
      },
      {
        // TODO: revisar que hace
        name: 'Entregas',
        link: 'entregas',
        icon: IconSales,
        info: 'Ventas totales entregadas por medio del software',
        show: module === 11000,
      },
      {
        name: 'Historial de inventario',
        link: 'inventario',
        icon: IconInventory,
        info: 'Historial de inventario',
        // show: inventoryHistory,
        show: false,
        feature: 'inventory',
      },
      {
        name: 'Ventas por usuarios',
        link: 'usuarios',
        icon: IconUsers,
        info: 'Ingresos y Egresos de los Usuarios',
        show: inOutUser,
        feature: 'reportsByUser',
        bullets: [
          'Tabla con el detalle de cada usuario',
          'Total de ventas, utilidad, gastos y depósitos por usuario',
          'Compara el desempeño entre tu equipo',
        ],
      },
      {
        name: 'Ventas totales',
        link: 'ventas/totales',
        icon: IconSales,
        info: 'Ventas totales',
        show: totalSell,
        bullets: [
          'Total de ventas presentado en gráfica de barras',
          'Agrupa por día, semana o mes',
          'Identifica tendencias y temporadas de tu negocio',
        ],
      },
      {
        name: 'Patrimonial',
        link: 'patrimonial',
        icon: IconActPas,
        info: 'Patrimonial',
        show: !isLimit && patrimonial,
        bullets: [
          'Tabla con un snapshot de cada cuenta monetaria',
          'Muestra cómo cada transacción afecta las cuentas de la empresa',
          'Rastrea el movimiento de tu patrimonio en el tiempo',
        ],
      },
      {
        name: 'Reducciones de inventario',
        link: 'merma',
        icon: IconWaste,
        info: 'Reducciones de inventario',
        show: waste,
        feature: 'inventory',
        bullets: [
          'Detalle de cada ítem mermado en el periodo que elijas',
          'Gráfica de barras ordenada de mayor a menor',
          'Ranking por monto para identificar tus mayores pérdidas de inventario',
        ],
      },
      {
        name: 'Gastos',
        link: 'gastos',
        icon: IconExpenses,
        info: 'Gastos',
        show: !isLimit && expense,
        bullets: [
          'Detalle de cada ítem de gasto en el periodo que elijas',
          'Gráfica de barras ordenada de mayor a menor',
          'Ranking por monto para identificar tus mayores gastos',
        ],
      },
      {
        name: 'Clientes',
        link: 'balance',
        icon: IconClients,
        info: 'Cuentas por Cobrar',
        show: !isLimit && clients,
        bullets: [
          'Gráfica y tabla del total de venta por cliente',
          'Filtra por el periodo de tiempo que elijas',
          'Ordenado de mayor a menor para ver tus mejores clientes',
        ],
      },
      {
        name: 'Compras',
        link: 'compras',
        icon: IconExpenses,
        info: 'Compras',
        show: !isLimit && purchase,
        bullets: [
          'Detalle de cada ítem comprado en el periodo que elijas',
          'Gráfica de barras ordenada de mayor a menor',
          'Ranking por monto para identificar tus mayores compras',
        ],
      },
      {
        name: 'Bitácora de Clientes',
        link: 'logs',
        icon: IconClients,
        info: 'Bitácora de todos los clientes de la empresa',
        show: false,
      },
      {
        name: 'Reportes por segmentación y presentación',
        link: 'segmentación',
        icon: IconInventory,
        info: 'Visualización y descarga de reportes con segmentación de categorías con distintas presentaciones',
        show: segmentation,
        feature: 'reportsSegmented',
        bullets: [
          'Varios reportes de ventas: por orden, por cliente, por producto y cuentas por cobrar',
          'Segmentados por categorías de cliente en el periodo que elijas',
          'Descarga cada reporte en XLSX, incluye ítems facturados (DTE)',
        ],
      },
      {
        name: 'Certificar de DTE',
        link: '',
        icon: IconInvoice,
        info: 'abc',
        show: false,
        onClick: () => setConciliationReport({ show: true, nullify: false }),
      },
      {
        name: 'Anular de DTE',
        link: '',
        icon: IconInvoice,
        info: 'abc',
        show: false,
        onClick: () => setConciliationReport({ show: true, nullify: true }),
      },
      {
        name: 'Ventas por sucursal',
        link: 'sucursales',
        icon: IconSales,
        info: 'Visualización y descarga de reportes de ventas segmentado por puntos de venta, bodegas o usuarios',
        show: branchSales,
        bullets: [
          'Ventas segmentadas por bodega, punto de venta o usuario',
          'Total, costo, utilidad, rentabilidad, clientes y ticket promedio por fila',
          'Filtra por periodo y tipo de pago; descarga en XLSX',
        ],
      },
      {
        name: 'Control de vehículos',
        link: 'vehiculos',
        icon: ParkingIcon,
        info: 'Histórico de entrada y salida de vehículos',
        show: true,
        bullets: [
          'Control de ventas de un parqueo por vehículo',
          'Placas, tarifa, fechas de ingreso/salida y monto por orden',
          'Filtra por tarifa, punto de venta y responsable; descarga en XLSX',
        ],
      },
    ]
  }

  // @ts-ignore
  return (
    <div>
      <Title title={'Reportes'} />
      <Row>
        {getReports()
          .filter(f => f.show)
          .map((report, i) => {
            const available = report.feature ? planPermissions[report.feature] : true
            return (
              <Col key={i} xl={4} lg={4} md={6} sm={6} xs={12} className={'mb-3'}>
                <ReportCard
                  icon={report.icon}
                  title={report.name}
                  bullets={report.bullets || [report.info]}
                  available={available}
                  to={available && report.link ? report.link : undefined}
                  onClick={report.onClick}
                  requiredPlans={
                    report.feature ? plansForFeature(report.feature) : undefined
                  }
                />
              </Col>
            )
          })}
      </Row>

      <Modal
        show={conciliationReport.show}
        centered
        onHide={() => setConciliationReport({ ...conciliationReport, show: false })}>
        <Modal.Header closeButton>
          <Modal.Title>Conciliación</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {/* @ts-ignore */}
          <CSVLoaderV2
            category={
              conciliationReport.nullify
                ? CATEGORY_UPLOAD.INVOICE_CONCILIATION_NULLIFY
                : CATEGORY_UPLOAD.INVOICE_CONCILIATION_NULLIFY
            }
            title={'Cargar CSV de Facturas'}
            onSuccess={() =>
              setConciliationReport({ ...conciliationReport, show: false })
            }
          />
        </Modal.Body>
      </Modal>
    </div>
  )
}

export default ReportsDashboard
